import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Package, RefreshCw, Send, Plus, Trash2, Upload, X, ChevronDown, ChevronUp, Loader2, Settings, FileText, AlertTriangle } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const DIVISORS = [5000, 4500, 4000, 2500];

const PINCODE_CITY_MAP = {
  "110001": "New Delhi", "110002": "New Delhi", "110003": "New Delhi", "110005": "New Delhi",
  "400001": "Mumbai", "400002": "Mumbai", "400003": "Mumbai", "400050": "Mumbai", "400051": "Mumbai",
  "560001": "Bangalore", "560002": "Bangalore", "560003": "Bangalore", "560004": "Bangalore",
  "600001": "Chennai", "600002": "Chennai", "600003": "Chennai", "600004": "Chennai",
  "700001": "Kolkata", "700002": "Kolkata", "700003": "Kolkata", "700004": "Kolkata",
  "500001": "Hyderabad", "500002": "Hyderabad", "500003": "Hyderabad", "500004": "Hyderabad",
  "411001": "Pune", "411002": "Pune", "411003": "Pune", "411004": "Pune",
  "380001": "Ahmedabad", "380002": "Ahmedabad", "380003": "Ahmedabad", "380004": "Ahmedabad",
  "302001": "Jaipur", "302002": "Jaipur", "302003": "Jaipur", "302004": "Jaipur",
  "226001": "Lucknow", "226002": "Lucknow", "226003": "Lucknow", "226004": "Lucknow",
  "440001": "Nagpur", "440002": "Nagpur", "440003": "Nagpur", "440004": "Nagpur",
  "682001": "Kochi", "682002": "Kochi", "682003": "Kochi", "682004": "Kochi",
  "641001": "Coimbatore", "641002": "Coimbatore", "641003": "Coimbatore",
  "530001": "Visakhapatnam", "530002": "Visakhapatnam", "530003": "Visakhapatnam",
  "520001": "Vijayawada", "520002": "Vijayawada", "520003": "Vijayawada",
  "516001": "Kadapa", "516002": "Kadapa", "516003": "Kadapa",
  "522001": "Guntur", "522002": "Guntur", "522003": "Guntur",
  "524001": "Nellore", "524002": "Nellore", "524003": "Nellore",
  "533001": "Rajahmundry", "533002": "Rajahmundry", "533003": "Rajahmundry",
  "534001": "Eluru", "534002": "Eluru",
  "517001": "Tirupati", "517002": "Tirupati",
  "515001": "Anantapur", "515002": "Anantapur",
  "518001": "Kurnool", "518002": "Kurnool",
};

const CITY_PINCODE_MAP = {
  "new delhi": "110001", "delhi": "110001",
  "mumbai": "400001", "bombay": "400001",
  "bangalore": "560001", "bengaluru": "560001",
  "chennai": "600001", "madras": "600001",
  "kolkata": "700001", "calcutta": "700001",
  "hyderabad": "500001",
  "pune": "411001",
  "ahmedabad": "380001",
  "jaipur": "302001",
  "lucknow": "226001",
  "nagpur": "440001",
  "kochi": "682001", "cochin": "682001",
  "coimbatore": "641001",
  "visakhapatnam": "530001", "vizag": "530001",
  "vijayawada": "520001",
  "kadapa": "516001", "cuddapah": "516001",
  "guntur": "522001",
  "nellore": "524001",
  "rajahmundry": "533001",
  "eluru": "534001",
  "tirupati": "517001",
  "anantapur": "515001",
  "kurnool": "518001",
};

function calcVolWt(l, b, h, qty, divisor) {
  const lv = parseFloat(l) || 0, bv = parseFloat(b) || 0,
    hv = parseFloat(h) || 0, qv = parseFloat(qty) || 1, dv = parseFloat(divisor) || 5000;
  if (!lv || !bv || !hv) return 0;
  return parseFloat(((lv * bv * hv * qv) / dv).toFixed(2));
}

const cleanPhone = (val) => {
  // Remove all non-digits
  let digits = val.replace(/\D/g, "");
  // Remove leading 91 if 12 digits starting with 91
  if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.slice(2);
  }
  // Remove leading 0 if 11 digits
  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  // Cap at 10 digits
  return digits.slice(0, 10);
};

const Field = ({ label, children, required, error }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-100 bg-white";
const inputErrorCls = "w-full px-3 py-2 border border-red-300 rounded-sm text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-100 bg-white";
const selectCls = "w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-white";

export default function BookingConsole() {
  const navigate = useNavigate();
  const photoRef = useRef();

  // ── Account & Pickup Mode ──
  const [accountType, setAccountType] = useState("domestic");
  const [pickupMode, setPickupMode] = useState("schedule"); // "schedule" | "counter"

  // ── Warehouse / Pickup Location ──
  const [warehouse, setWarehouse] = useState("");
  const [warehouseList, setWarehouseList] = useState([]);
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [pendingWarehouse, setPendingWarehouse] = useState("");
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: "", address: "", city: "",
    pincode: "", contact_person: "",
    contact_phone: ""
  });
  const [savingLocation, setSavingLocation] = useState(false);

  // ── Pickup scheduling ──
  const [pickupDate, setPickupDate] = useState(0);
  const [pickupSlot, setPickupSlot] = useState("EVENING");
  const [pickupConfirmed, setPickupConfirmed] = useState(false);
  const [pickupContact, setPickupContact] = useState("");
  const [pickupPhone, setPickupPhone] = useState("");

  // ── FROM / TO ──
  const [fromCity, setFromCity] = useState("");
  const [fromPincode, setFromPincode] = useState("");
  const [toCity, setToCity] = useState("");
  const [toPincode, setToPincode] = useState("");
  const [fromPincodeError, setFromPincodeError] = useState("");
  const [toPincodeError, setToPincodeError] = useState("");

  // ── Sender ──
  const [senderName, setSenderName] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [senderAddress, setSenderAddress] = useState("");

  // ── Receiver ──
  const [receiverName, setReceiverName] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");
  const [receiverAddress, setReceiverAddress] = useState("");

  // ── Seller/Vendor ──
  const [showSeller, setShowSeller] = useState(false);
  const [sellerName, setSellerName] = useState("");
  const [sellerPhone, setSellerPhone] = useState("");
  const [sellerGST, setSellerGST] = useState("");

  // ── Shipment Details ──
  const [mode, setMode] = useState("surface");
  const [paymentMode, setPaymentMode] = useState("prepaid");
  const [consignmentType, setConsignmentType] = useState("non-document");
  const [materialDesc, setMaterialDesc] = useState("");
  const [consignmentValue, setConsignmentValue] = useState("");
  const [initialWeight, setInitialWeight] = useState("");
  const [codAmount, setCodAmount] = useState("");

  // ── Product & Service ──
  const [productCategory, setProductCategory] = useState("");
  const [productCategories, setProductCategories] = useState([]);
  const [serviceType, setServiceType] = useState("STANDARD");

  // ── Insurance ──
  const [insuranceType, setInsuranceType] = useState("OWNER_RISK");
  const insurancePremium = insuranceType === "CARRIER_RISK"
    ? Math.round((parseFloat(consignmentValue) || 0) * 0.005)
    : 0;

  // ── Eway Bill ──
  const [ewayBill, setEwayBill] = useState("");
  const requiresEwayBill = (parseFloat(consignmentValue) || 0) > 50000;

  // ── Weight & Dimensions ──
  const [boxMode, setBoxMode] = useState("single");
  const [addLater, setAddLater] = useState(false);
  const [deadWeight, setDeadWeight] = useState("");
  const [length, setLength] = useState("");
  const [breadth, setBreadth] = useState("");
  const [height, setHeight] = useState("");
  const [divisor, setDivisor] = useState(5000);
  const [boxes, setBoxes] = useState([{ id: 1, qty: 1, l: "", b: "", h: "", divisor: 4000, deadWt: "" }]);
  const [sameSize, setSameSize] = useState(false);
  const [sameSizeData, setSameSizeData] = useState({ l: "", b: "", h: "", divisor: 4000, deadWt: "" });
  const [totalBoxCount, setTotalBoxCount] = useState(1);

  // ── Photos ──
  const [photos, setPhotos] = useState([]);

  // ── Billing ──
  const [billOverride, setBillOverride] = useState("");
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [rateDetails, setRateDetails] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // ── Special Instructions ──
  const [specialInstructions, setSpecialInstructions] = useState("");

  // ── Customers ──
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", phone: "", password: "Customer@123" });
  const [savingCustomer, setSavingCustomer] = useState(false);

  // ── Order ID ──
  const [orderId, setOrderId] = useState("");
  const [fetchingOrderId, setFetchingOrderId] = useState(true);
  const [loading, setLoading] = useState(false);

  // ── AWB count ──
  const totalAwbCount = boxMode === "multi"
    ? sameSize
      ? totalBoxCount
      : boxes.reduce((sum, b) => sum + (parseInt(b.qty) || 1), 0)
    : 1;

  useEffect(() => {
    fetchCustomers();
    fetchWarehouses();
    fetchNextOrderId();
    fetchProductCategories();
  }, []);

  const fetchNextOrderId = async () => {
    try {
      const res = await api.get("/shipments/next-order-id");
      setOrderId(res.data.next_order_id);
    } catch { setOrderId("--"); }
    finally { setFetchingOrderId(false); }
  };

  const fetchCustomers = async (q = "") => {
    try {
      const params = { role: "customer" };
      if (q) params.search = q;
      const res = await api.get("/users", { params });
      setCustomers(res.data || []);
    } catch { }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await api.get("/warehouses");
      setWarehouseList(res.data || []);
    } catch { }
  };

  const fetchCustomerPickupLocations = async (customerId) => {
    try {
      const res = await api.get(`/v2/pickup-locations/list`, {
        params: { owner_id: customerId, owner_type: "CUSTOMER" }
      });
      if (res.data && res.data.length > 0) {
        setWarehouseList(res.data.map(p => ({
          id: p.location_code,
          code: p.location_code || "LOC",
          name: p.location_name,
          address: p.address_line1,
          city: p.city,
          pincode: p.pincode,
          contact_person: p.contact_person || "",
          contact_phone: p.contact_mobile || "",
          customer_id: customerId
        })));
      } else {
        fetchWarehouses();
      }
    } catch {
      fetchWarehouses();
    }
  };
  const fetchProductCategories = async () => {
    try {
      const res = await api.get("/v2/products/categories");
      setProductCategories(res.data || []);
    } catch { }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email || !newCustomer.phone) {
      toast.error("Please fill Name, Email and Phone");
      return;
    }
    setSavingCustomer(true);
    try {
      // Path B: POST /api/v2/customers/create (v2 field names). Quick-add
      // omits KYC; admin will complete via /admin/customers/new later.
      const v2Payload = {
        customer_type: "retail",
        contact_person: newCustomer.name,
        mobile: newCustomer.phone,
        email: newCustomer.email,
        password: newCustomer.password || "Customer@123",
      };
      const res = await api.post("/v2/customers/create", v2Payload);
      toast.success("Customer created!");
      const created = {
        id: res.data.id || res.data._id || res.data.customer_id,
        name: newCustomer.name,
        email: newCustomer.email,
        phone: newCustomer.phone
      };
      setSelectedCustomer(created);
      setCustomerSearch("");
      setShowCustomerDropdown(false);
      setShowAddCustomer(false);
      setNewCustomer({ name: "", email: "", phone: "", password: "Customer@123" });
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create customer");
    } finally {
      setSavingCustomer(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const q = customerSearch.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.phone?.includes(q) || c.email?.toLowerCase().includes(q);
  });

  const isValidPincode = (pin) => /^\d{6}$/.test(pin);

  const handleFromPincodeChange = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 6);
    setFromPincode(cleaned);
    if (cleaned.length === 6) {
      setFromPincodeError("");
      api.get(`/v2/pincode-master/lookup/${cleaned}`)
        .then(r => { if (r.data?.city) setFromCity(r.data.city); })
        .catch(() => {
          const city = PINCODE_CITY_MAP[cleaned];
          if (city) setFromCity(city);
        });
    } else if (cleaned.length > 0) {
      setFromPincodeError("Pincode must be 6 digits");
    } else {
      setFromPincodeError("");
    }
  };

  const handleToPincodeChange = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 6);
    setToPincode(cleaned);
    if (cleaned.length === 6) {
      setToPincodeError("");
      api.get(`/v2/pincode-master/lookup/${cleaned}`)
        .then(r => { if (r.data?.city) setToCity(r.data.city); })
        .catch(() => {
          const city = PINCODE_CITY_MAP[cleaned];
          if (city) setToCity(city);
        });
    } else if (cleaned.length > 0) {
      setToPincodeError("Pincode must be 6 digits");
    } else {
      setToPincodeError("");
    }
  };

  const handleFromCityChange = (value) => {
    setFromCity(value);
    const pincode = CITY_PINCODE_MAP[value.toLowerCase().trim()];
    if (pincode) { setFromPincode(pincode); setFromPincodeError(""); }
  };

  const handleToCityChange = (value) => {
    setToCity(value);
    const pincode = CITY_PINCODE_MAP[value.toLowerCase().trim()];
    if (pincode) { setToPincode(pincode); setToPincodeError(""); }
  };

  // Weight calculations
  const singleVolWt = calcVolWt(length, breadth, height, 1, divisor);
  const singleDeadWt = parseFloat(deadWeight) || 0;
  const singleChargeableWt = Math.max(singleDeadWt, singleVolWt);
  const multiTotalDeadWt = boxes.reduce((s, b) => s + ((parseFloat(b.deadWt) || 0) * (parseInt(b.qty) || 1)), 0);
  const multiTotalVolWt = boxes.reduce((s, b) => s + calcVolWt(b.l, b.b, b.h, b.qty, b.divisor), 0);
  const multiChargeableWt = Math.max(multiTotalDeadWt, multiTotalVolWt);

  // Same-size multi box weight
  const sameSizeVolWt = calcVolWt(sameSizeData.l, sameSizeData.b, sameSizeData.h, totalBoxCount, sameSizeData.divisor);
  const sameSizeDeadWt = (parseFloat(sameSizeData.deadWt) || 0) * totalBoxCount;
  const sameSizeChargeableWt = Math.max(sameSizeDeadWt, sameSizeVolWt);

  const finalChargeableWt = addLater
    ? (parseFloat(initialWeight) || 0)
    : boxMode === "single"
      ? singleChargeableWt
      : sameSize
        ? sameSizeChargeableWt
        : multiChargeableWt;

  // Rate calculation
  const calculatePrice = async () => {
    if (!finalChargeableWt || !fromPincode || !toPincode) return;
    try {
      const res = await api.post("/v2/rate-calculator/calculate", {
        origin_pincode: fromPincode,
        destination_pincode: toPincode,
        weight_kg: finalChargeableWt,
        service_type: mode === "air" ? "EXPRESS" : "SURFACE",
        customer_type: accountType === "b2b" ? "B2B" : "B2C",
        customer_id: selectedCustomer?.id || null,
      });
      setRateDetails(res.data);
      setSelectedVendor(null);
      setEstimatedPrice(res.data.customer_price || res.data.vendor_cost || null);
    } catch {
      setRateDetails(null);
      try {
        const res = await api.post("/pricing/calculate", { weight: finalChargeableWt, origin_pincode: fromPincode, destination_pincode: toPincode });
        setEstimatedPrice(res.data.price);
      } catch { setEstimatedPrice(null); }
    }
  };

  useEffect(() => {
    if (finalChargeableWt && fromPincode && toPincode) calculatePrice();
  }, [finalChargeableWt, fromPincode, toPincode]);

  const addBox = () => setBoxes([...boxes, { id: Date.now(), qty: 1, l: "", b: "", h: "", divisor: 4000, deadWt: "" }]);
  const updateBox = (id, field, value) => setBoxes(boxes.map(b => b.id === id ? { ...b, [field]: value } : b));
  const removeBox = (id) => { if (boxes.length > 1) setBoxes(boxes.filter(b => b.id !== id)); };
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setPhotos([...photos, ...files.map(f => ({ name: f.name, url: URL.createObjectURL(f) }))]);
  };

  const finalBillAmount = billOverride ? parseFloat(billOverride) : estimatedPrice;

  const handleReset = () => {
    setSenderName(""); setSenderPhone(""); setSenderAddress("");
    setReceiverName(""); setReceiverPhone(""); setReceiverAddress("");
    setFromCity(""); setFromPincode(""); setToCity(""); setToPincode("");
    setFromPincodeError(""); setToPincodeError("");
    setMaterialDesc(""); setConsignmentValue(""); setCodAmount(""); setInitialWeight("");
    setDeadWeight(""); setLength(""); setBreadth(""); setHeight(""); setDivisor(5000);
    setBoxes([{ id: 1, qty: 1, l: "", b: "", h: "", divisor: 4000, deadWt: "" }]);
    setPhotos([]); setBillOverride(""); setSpecialInstructions("");
    setSelectedCustomer(null); setEstimatedPrice(null); setRateDetails(null);
    setWarehouse(""); setPickupDate(0); setPickupSlot("EVENING");
    setPickupContact(""); setPickupPhone("");
    setInsuranceType("OWNER_RISK"); setEwayBill("");
    setProductCategory(""); setServiceType("STANDARD");
    setSameSize(false); setTotalBoxCount(1);
    toast.info("Form reset.");
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) { toast.error("Please select a customer first."); return; }
    if (!senderName || !receiverName) { toast.error("Sender and receiver names are required."); return; }
    if (!senderPhone || senderPhone.length !== 10) {
      toast.error("Sender phone must be 10 digits");
      return;
    }
    if (!receiverPhone || receiverPhone.length !== 10) {
      toast.error("Receiver phone must be 10 digits");
      return;
    }
    if (!senderAddress || !senderAddress.trim()) {
      toast.error("Sender address is required");
      return;
    }
    if (!receiverAddress || !receiverAddress.trim()) {
      toast.error("Receiver address is required");
      return;
    }
    if (pickupMode === "schedule" && !pickupConfirmed) {
      toast.error("Please confirm pickup date and slot first");
      return;
    }
    if (!fromCity || !toCity) { toast.error("Origin and destination cities are required."); return; }
    if (!fromPincode || !isValidPincode(fromPincode)) {
      setFromPincodeError("Valid 6-digit pincode is required");
      toast.error("Origin pincode must be a valid 6-digit number."); return;
    }
    if (!toPincode || !isValidPincode(toPincode)) {
      setToPincodeError("Valid 6-digit pincode is required");
      toast.error("Destination pincode must be a valid 6-digit number."); return;
    }
    if (requiresEwayBill && !ewayBill.trim()) {
      toast.error("Eway Bill number is required for shipments above ₹50,000"); return;
    }
    if (pickupMode === "schedule" && !warehouse) {
      toast.error("Please select a pickup location."); return;
    }

    setLoading(true);
    try {
      const finalPrice = billOverride ? parseFloat(billOverride) : estimatedPrice;
      const selectedWH = warehouseList.find(w => w.id === warehouse);

      const payload = {
        customer_id: selectedCustomer.id,
        account_type: accountType,
        pickup_mode: pickupMode,
        warehouse: selectedWH?.code || warehouse,
        warehouse_address: selectedWH ? `${selectedWH.name}, ${selectedWH.address}, ${selectedWH.city} - ${selectedWH.pincode}` : "",
        shipping_mode: mode,
        payment_mode: paymentMode,
        consignment_type: consignmentType,
        material_description: materialDesc,
        consignment_value: consignmentValue ? parseFloat(consignmentValue) : null,
        cod_amount: paymentMode === "cod" && codAmount ? parseFloat(codAmount) : null,
        product_category: productCategory || null,
        service_type: serviceType,
        insurance_type: insuranceType,
        insurance_premium: insurancePremium,
        eway_bill_number: ewayBill || null,
        volumetric_weight: boxMode === "single" ? singleVolWt : parseFloat(multiTotalVolWt.toFixed(2)),
        chargeable_weight: parseFloat(finalChargeableWt.toFixed(2)),
        box_count: totalAwbCount,
        same_size_boxes: boxMode === "multi" && sameSize ? {
          count: totalBoxCount,
          length_cm: parseFloat(sameSizeData.l) || 0,
          breadth_cm: parseFloat(sameSizeData.b) || 0,
          height_cm: parseFloat(sameSizeData.h) || 0,
          dead_weight_per_box: parseFloat(sameSizeData.deadWt) || 0,
          divisor: sameSizeData.divisor
        } : null,
        bill_amount_override: billOverride ? parseFloat(billOverride) : null,
        special_instructions: specialInstructions,
        seller_details: showSeller ? { name: sellerName, phone: sellerPhone, gst: sellerGST } : null,
        boxes: boxMode === "multi" && !sameSize ? boxes : undefined,
        pickup_date_offset: pickupMode === "schedule" ? pickupDate : null,
        pickup_slot: pickupMode === "schedule" ? pickupSlot : null,
        pickup_contact_name: pickupContact || null,
        pickup_contact_phone: pickupPhone || null,
        origin: { name: senderName, phone: senderPhone, address: senderAddress, city: fromCity, pincode: fromPincode },
        destination: { name: receiverName, phone: receiverPhone, address: receiverAddress, city: toCity, pincode: toPincode },
        package: { weight: finalChargeableWt, type: consignmentType, description: materialDesc }
      };

      const res = await api.post("/shipments", payload);
      toast.success(`Order booked! AWB: ${res.data.order_id} (${totalAwbCount} label${totalAwbCount > 1 ? "s" : ""})`);
      navigate("/staff/orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedWH = React.useMemo(() => 
    warehouseList.find(w => w.id === warehouse),
    [warehouseList, warehouse]
  );

  return (
    <DashboardLayout>
      <div className="max-w-5xl space-y-5 pb-10">

        {/* ── HEADER: Order ID ── */}
        <div className="flex items-center justify-between bg-white rounded-lg border border-slate-200 p-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">New Booking Console</h1>
            <p className="text-slate-500 text-sm mt-0.5">Create a new shipment order with all details.</p>
          </div>
          <div className="bg-brand-50 px-5 py-3 rounded-lg border border-brand-200 text-center">
            <p className="text-xs text-slate-500 font-semibold uppercase">Order ID</p>
            <p className="font-heading font-bold text-2xl text-brand-600" data-testid="order-id-display">
              {fetchingOrderId ? "..." : orderId}
            </p>
            <p className="text-xs text-slate-400 mt-1">Format: DDMMYY + Serial</p>
          </div>
        </div>

        {/* ── ROW 1: Account Type + Pickup Mode (two separate cards side by side) ── */}
        <div className="grid grid-cols-2 gap-5">
          {/* Account Type */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">Account Type</h2>
            <div className="flex gap-2 flex-wrap">
              {["domestic", "international", "other"].map((type) => (
                <button key={type} onClick={() => setAccountType(type)} data-testid={`account-type-${type}`}
                  className={`px-5 py-2 rounded-sm text-sm font-semibold uppercase tracking-wide transition-all ${accountType === type ? "bg-brand-500 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Pickup Mode */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">Pickup Mode</h2>
            <div className="flex rounded-sm border border-slate-200 overflow-hidden w-fit">
              {[["schedule", "Schedule Pickup"], ["counter", "Counter Drop"]].map(([val, lbl]) => (
                <button key={val} onClick={() => { setPickupMode(val); if (val === "counter") setPickupConfirmed(false); }}
                  className={`px-5 py-2 text-sm font-semibold transition-colors ${pickupMode === val ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {pickupMode === "counter" && (
              <p className="text-xs text-slate-400 mt-2">
                Customer drops shipment at nearest Sendtoo point. Can be converted to scheduled pickup before first FM scan.
              </p>
            )}
          </div>
        </div>

        {/* ── ROW 2: Customer (left) + Pickup Location (right) ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* LEFT: Customer */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-500">
                  Customer (Billing Account)<span className="text-red-500 ml-0.5">*</span>
                </h2>
                <button onClick={() => setShowAddCustomer(!showAddCustomer)}
                  className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-semibold">
                  <Plus className="w-3.5 h-3.5" />
                  {showAddCustomer ? "Cancel" : "Add New"}
                </button>
              </div>

              {/* Inline Add Customer Form */}
              {showAddCustomer && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
                  <h3 className="font-semibold text-xs text-slate-700 mb-2">Add New Customer</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Full Name" required>
                      <input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                        placeholder="Customer name" className={inputCls} data-testid="new-customer-name" />
                    </Field>
                    <Field label="Email" required>
                      <input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        placeholder="email@example.com" className={inputCls} data-testid="new-customer-email" />
                    </Field>
                    <Field label="Phone" required>
                      <input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                        placeholder="+91 9876543210" className={inputCls} data-testid="new-customer-phone" />
                    </Field>
                    <Field label="Password">
                      <input value={newCustomer.password} onChange={(e) => setNewCustomer({ ...newCustomer, password: e.target.value })}
                        placeholder="Customer@123" className={inputCls} />
                    </Field>
                  </div>
                  <button onClick={handleAddCustomer} disabled={savingCustomer}
                    className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-50"
                    data-testid="save-customer-btn">
                    {savingCustomer ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Save Customer
                  </button>
                </div>
              )}

              {/* Customer Search */}
              <div className="relative">
                <input value={customerSearch}
                  onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  placeholder="Search by name, phone, or email..."
                  className={inputCls} data-testid="booking-customer-search" />

                {selectedCustomer && (
                  <div className="mt-2 bg-brand-50 border border-brand-100 rounded-sm p-2 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-brand-700">{selectedCustomer.name}</p>
                      <p className="text-xs text-slate-400">{selectedCustomer.email}</p>
                    </div>
                    <button onClick={() => { setSelectedCustomer(null); setWarehouse(""); setCustomerSearch(""); setShowAddLocation(false); fetchWarehouses(); }}
                      className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                )}

                {showCustomerDropdown && !selectedCustomer && filteredCustomers.length > 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {filteredCustomers.map(c => (
                      <button key={c.id} onClick={() => { setSelectedCustomer(c); fetchCustomerPickupLocations(c.id); setCustomerSearch(""); setShowCustomerDropdown(false); setShowAddLocation(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-brand-50 text-left">
                        <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold">{c.name?.[0]}</div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                          <p className="text-xs text-slate-400">{c.email}{c.phone && ` • ${c.phone}`}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {showCustomerDropdown && !selectedCustomer && filteredCustomers.length === 0 && customerSearch && (
                  <div className="absolute z-20 top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 p-4 text-center">
                    <p className="text-sm text-slate-500">No customer found</p>
                    <button onClick={() => setShowAddCustomer(true)} className="text-sm text-brand-500 hover:underline mt-1">+ Add New Customer</button>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Pickup Location */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-500">
                  Pickup Location / Warehouse
                  {pickupMode === "schedule" && <span className="text-red-500 ml-0.5">*</span>}
                </h2>
                {pickupMode === "schedule" && selectedCustomer && (
                  <button onClick={() => setShowAddLocation(!showAddLocation)}
                    className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-semibold">
                    <Plus className="w-3.5 h-3.5" />
                    {showAddLocation ? "Cancel" : "Add New"}
                  </button>
                )}
              </div>

              {/* Counter Drop state */}
              {pickupMode === "counter" ? (
                <div className="flex items-center justify-center h-28 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-400 text-center px-6">
                    Counter Drop — no pickup needed.
                    <span className="block text-xs mt-1 text-slate-300">Can be converted to scheduled pickup before first FM scan.</span>
                  </p>
                </div>

              ) : !selectedCustomer ? (
                /* No customer selected yet */
                <div className="flex items-center justify-center h-28 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-400 text-center px-6">Select a customer first to load pickup locations</p>
                </div>

              ) : (
                /* Customer selected + Schedule Pickup */
                <div className="space-y-4">
                  <select value={warehouse}
                    onChange={(e) => {
                      const sel = warehouseList.find(w => w.id === e.target.value);
                      const isLinked = !sel?.customer_id || sel?.customer_id === selectedCustomer?.id;
                      if (e.target.value && !isLinked) {
                        setPendingWarehouse(e.target.value);
                        setShowLocationWarning(true);
                      } else {
                        setWarehouse(e.target.value);
                        setPickupConfirmed(false);
                        setPickupContact(sel?.contact_person || "");
                        setPickupPhone(sel?.contact_phone || "");
                      }
                    }}
                    className={selectCls} data-testid="warehouse-select">
                    <option value="">— Select Pickup Location —</option>
                    {warehouseList.map(w => (
                      <option key={w.id} value={w.id}>{w.code} - {w.name} ({w.city})</option>
                    ))}
                  </select>

                  {showAddLocation && selectedCustomer && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-2" data-testid="add-location-form">
                      <h3 className="text-xs font-bold text-slate-700 mb-2">
                        Add Location for {selectedCustomer.name}
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Location Name" required>
                          <input value={newLocation.name}
                            onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                            placeholder="e.g. Main Office"
                            className={inputCls}
                            data-testid="new-location-name" />
                        </Field>
                        <Field label="Pincode" required>
                          <input value={newLocation.pincode}
                            onChange={(e) => setNewLocation({ ...newLocation, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                            placeholder="530001"
                            maxLength={6}
                            className={inputCls}
                            data-testid="new-location-pincode" />
                        </Field>
                        <Field label="City" required>
                          <input value={newLocation.city}
                            onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                            placeholder="Visakhapatnam"
                            className={inputCls}
                            data-testid="new-location-city" />
                        </Field>
                        <Field label="Contact Phone">
                          <input value={newLocation.contact_phone}
                            onChange={(e) => setNewLocation({ ...newLocation, contact_phone: cleanPhone(e.target.value) })}
                            placeholder="9876543210"
                            maxLength={10}
                            className={inputCls}
                            data-testid="new-location-phone" />
                        </Field>
                        <div className="col-span-2">
                          <Field label="Full Address" required>
                            <input value={newLocation.address}
                              onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                              placeholder="Street, landmark..."
                              className={inputCls}
                              data-testid="new-location-address" />
                          </Field>
                        </div>
                        <div className="col-span-2">
                          <Field label="Contact Person">
                            <input value={newLocation.contact_person}
                              onChange={(e) => setNewLocation({ ...newLocation, contact_person: e.target.value })}
                              placeholder="Person name"
                              className={inputCls}
                              data-testid="new-location-contact-person" />
                          </Field>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!newLocation.name || !newLocation.address || !newLocation.city || !newLocation.pincode) {
                            toast.error("Name, address, city and pincode are required");
                            return;
                          }
                          setSavingLocation(true);
                          try {
                            await api.post(
                              `/v2/pickup-locations/create`,
                              {
                                owner_id: selectedCustomer.id,
                                owner_type: "CUSTOMER",
                                location_name: newLocation.name,
                                contact_person: newLocation.contact_person,
                                contact_mobile: newLocation.contact_phone,
                                address_line1: newLocation.address,
                                address_line2: "",
                                city: newLocation.city,
                                pincode: newLocation.pincode,
                                state: "",
                                location_type: "WAREHOUSE"
                              }
                            );
                            setShowAddLocation(false);
                            fetchCustomerPickupLocations(selectedCustomer.id);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to save location");
                          } finally {
                            setSavingLocation(false);
                          }
                        }}
                        disabled={savingLocation}
                        data-testid="save-new-location-btn"
                        className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-50">
                        {savingLocation
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Plus className="w-3.5 h-3.5" />}
                        Save Location
                      </button>
                    </div>
                  )}

                  {/* Address preview */}
                  {selectedWH && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                      <p className="font-semibold text-slate-700">{selectedWH.name}</p>
                      <p className="text-slate-400 mt-0.5">{selectedWH.address}</p>
                      <p className="text-slate-400">{selectedWH.city} — {selectedWH.pincode}</p>
                    </div>
                  )}

                  {/* 7-day date pills — only after location selected */}
                  {(warehouse || selectedWH) && pickupMode === "schedule" && (
                    <>
                      {!pickupConfirmed ? (
                        <>
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-2">Pickup Date</label>
                            <div className="flex gap-1.5 flex-wrap">
                              {Array.from({ length: 7 }, (_, i) => {
                                const d = new Date();
                                d.setDate(d.getDate() + i);
                                return (
                                  <button key={i} onClick={() => setPickupDate(i)}
                                    className={`flex flex-col items-center px-2.5 py-1.5 rounded-full border-2 min-w-[44px] transition-all text-xs ${pickupDate === i ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                                    <span className="font-semibold">{d.toLocaleDateString("en-IN", { weekday: "short" })}</span>
                                    <span className="font-bold text-sm">{d.getDate()}</span>
                                    <span>{d.toLocaleDateString("en-IN", { month: "short" })}</span>
                                  </button>
                                );
                              })}
                            </div>
                            {mode === "ndd" && (
                              <p className="text-xs text-red-500 mt-1.5 font-semibold">NDD: ensure pickup before 6:00 PM</p>
                            )}
                          </div>

                          {/* Slot picker */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-2">Pickup Slot</label>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                ["MIDDAY", "☀️", "Mid Day", "10:00-14:00"],
                                ["EVENING", "🌙", "Evening", "14:00-18:00"],
                                ["LATE_EVENING", "🌃", "Late Evening", "18:00-21:00"]
                              ].map(([val, icon, lbl, time]) => (
                                <button key={val} onClick={() => setPickupSlot(val)}
                                  className={`flex flex-col items-center py-2 px-1 rounded-lg border-2 transition-all text-xs ${pickupSlot === val ? "border-brand-500 bg-brand-50 text-brand-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                                  <span className="text-base">{icon}</span>
                                  <span className="font-semibold">{lbl}</span>
                                  <span className="text-slate-400">{time}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Confirm button */}
                          <button
                            onClick={() => setPickupConfirmed(true)}
                            data-testid="confirm-pickup-btn"
                            className="mt-2 px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-sm hover:bg-brand-600">
                            Confirm Pickup
                          </button>
                        </>
                      ) : (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3 mt-2" data-testid="pickup-confirmed-banner">
                          <div>
                            <p className="text-xs font-semibold text-green-700">
                              Pickup Confirmed
                            </p>
                            <p className="text-sm font-bold text-slate-700 mt-0.5">
                              {(() => {
                                const d = new Date();
                                d.setDate(d.getDate() + pickupDate);
                                return d.toLocaleDateString("en-IN",
                                  { weekday: "short", day: "numeric", month: "short" });
                              })()}
                              {" · "}
                              {pickupSlot === "MIDDAY"
                                ? "☀️ Mid Day 10:00-14:00"
                                : pickupSlot === "EVENING"
                                ? "🌙 Evening 14:00-18:00"
                                : "🌃 Late Evening 18:00-21:00"}
                            </p>
                          </div>
                          <button
                            onClick={() => setPickupConfirmed(false)}
                            data-testid="edit-pickup-btn"
                            className="text-xs text-brand-500 hover:underline font-semibold">
                            Edit
                          </button>
                        </div>
                      )}

                      {/* Contact fields */}
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Contact Person">
                          <input value={pickupContact} onChange={(e) => setPickupContact(e.target.value)}
                            placeholder="Contact name" className={inputCls} />
                        </Field>
                        <Field label="Contact Phone">
                          <input value={pickupPhone} onChange={(e) => setPickupPhone(e.target.value)}
                            placeholder="9876543210" className={inputCls} />
                        </Field>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── ROUTE DETAILS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Route Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="From PIN Code" required error={fromPincodeError}>
              <input value={fromPincode} onChange={(e) => handleFromPincodeChange(e.target.value)}
                placeholder="600001" maxLength={6}
                className={fromPincodeError ? inputErrorCls : inputCls} data-testid="from-pincode" />
            </Field>
            <Field label="From City" required>
              <input value={fromCity} onChange={(e) => handleFromCityChange(e.target.value)}
                placeholder="Origin city" className={inputCls} data-testid="from-city" />
            </Field>
            <Field label="To PIN Code" required error={toPincodeError}>
              <input value={toPincode} onChange={(e) => handleToPincodeChange(e.target.value)}
                placeholder="110001" maxLength={6}
                className={toPincodeError ? inputErrorCls : inputCls} data-testid="to-pincode" />
            </Field>
            <Field label="To City" required>
              <input value={toCity} onChange={(e) => handleToCityChange(e.target.value)}
                placeholder="Destination city" className={inputCls} data-testid="to-city" />
            </Field>
          </div>
          <p className="text-xs text-slate-400 mt-3">Tip: Enter pincode to auto-fill city, or enter city name to auto-fill pincode.</p>
        </div>

        {/* ── CONSIGNER / CONSIGNEE ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Consigner / Sender</h2>
            <div className="space-y-3">
              <Field label="Full Name" required>
                <input value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Sender's full name" className={inputCls} data-testid="sender-name" />
              </Field>
              <Field label="Phone" required>
                <div className="flex">
                  <select className="px-2 py-2 border border-r-0 border-slate-200 rounded-l-sm text-sm bg-slate-50 text-slate-600 focus:outline-none">
                    <option>India (+91)</option><option>USA (+1)</option><option>UAE (+971)</option>
                  </select>
                  <input value={senderPhone}
                    onChange={(e) => setSenderPhone(cleanPhone(e.target.value))}
                    onPaste={(e) => {
                      e.preventDefault();
                      setSenderPhone(cleanPhone(e.clipboardData.getData("text")));
                    }}
                    maxLength={10}
                    placeholder="9876543210"
                    className={senderPhone && senderPhone.length < 10
                      ? "flex-1 px-3 py-2 border border-red-300 rounded-r-sm text-sm focus:outline-none"
                      : "flex-1 px-3 py-2 border border-slate-200 rounded-r-sm text-sm focus:outline-none focus:border-brand-500"}
                    data-testid="sender-phone" />
                </div>
              </Field>
              <Field label="Full Address" required>
                <textarea value={senderAddress} onChange={(e) => setSenderAddress(e.target.value)}
                  rows={3} placeholder="Complete address with landmark" className={`${inputCls} resize-none`} data-testid="sender-address" />
              </Field>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Consignee / Receiver</h2>
            <div className="space-y-3">
              <Field label="Full Name" required>
                <input value={receiverName} onChange={(e) => setReceiverName(e.target.value)} placeholder="Receiver's full name" className={inputCls} data-testid="receiver-name" />
              </Field>
              <Field label="Phone" required>
                <div className="flex">
                  <select className="px-2 py-2 border border-r-0 border-slate-200 rounded-l-sm text-sm bg-slate-50 text-slate-600 focus:outline-none">
                    <option>India (+91)</option><option>USA (+1)</option><option>UAE (+971)</option>
                  </select>
                  <input value={receiverPhone}
                    onChange={(e) => setReceiverPhone(cleanPhone(e.target.value))}
                    onPaste={(e) => {
                      e.preventDefault();
                      setReceiverPhone(cleanPhone(e.clipboardData.getData("text")));
                    }}
                    maxLength={10}
                    placeholder="9876543210"
                    className={receiverPhone && receiverPhone.length < 10
                      ? "flex-1 px-3 py-2 border border-red-300 rounded-r-sm text-sm focus:outline-none"
                      : "flex-1 px-3 py-2 border border-slate-200 rounded-r-sm text-sm focus:outline-none focus:border-brand-500"}
                    data-testid="receiver-phone" />
                </div>
              </Field>
              <Field label="Full Address" required>
                <textarea value={receiverAddress} onChange={(e) => setReceiverAddress(e.target.value)}
                  rows={3} placeholder="Complete address with landmark" className={`${inputCls} resize-none`} data-testid="receiver-address" />
              </Field>
            </div>
          </div>
        </div>

        {/* ── SELLER / VENDOR (collapsible) ── */}
        <div className="bg-white rounded-lg border border-slate-200">
          <button onClick={() => setShowSeller(!showSeller)} className="w-full flex items-center justify-between p-5 text-left">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500">
              Seller / Vendor Details <span className="text-slate-400 normal-case text-xs">(optional)</span>
            </h2>
            {showSeller ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {showSeller && (
            <div className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-slate-100">
              <Field label="Seller Name"><input value={sellerName} onChange={(e) => setSellerName(e.target.value)} placeholder="Vendor name" className={inputCls} data-testid="seller-name" /></Field>
              <Field label="Phone"><input value={sellerPhone} onChange={(e) => setSellerPhone(e.target.value)} placeholder="Phone number" className={inputCls} data-testid="seller-phone" /></Field>
              <Field label="GST Number"><input value={sellerGST} onChange={(e) => setSellerGST(e.target.value)} placeholder="GST number" className={inputCls} data-testid="seller-gst" /></Field>
            </div>
          )}
        </div>

        {/* ── SHIPMENT DETAILS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Shipment Details</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Shipping Mode */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Shipping Mode</label>
              <div className="flex gap-1">
                {[["surface", "Surface"], ["express", "Express"], ["ndd", "NDD"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => setMode(val)} data-testid={`mode-${val}`}
                    className={`flex-1 py-1.5 text-xs font-semibold rounded-sm transition-colors ${mode === val ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{lbl}</button>
                ))}
              </div>
            </div>
            {/* Payment Mode */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Payment Mode</label>
              <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className={selectCls} data-testid="payment-mode">
                <option value="prepaid">Prepaid</option>
                <option value="cod">COD</option>
                <option value="credit">Credit</option>
                <option value="to_pay">To Pay</option>
              </select>
            </div>
            {/* Consignment Type */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Consignment Type</label>
              <select value={consignmentType} onChange={(e) => setConsignmentType(e.target.value)} className={selectCls} data-testid="consignment-type">
                <option value="document">Document</option>
                <option value="non-document">Non-Document</option>
                <option value="others">Others</option>
              </select>
            </div>
            {/* Consignment Value */}
            <Field label="Consignment Value (₹)">
              <input type="number" value={consignmentValue} onChange={(e) => setConsignmentValue(e.target.value)}
                placeholder="0.00" className={inputCls} data-testid="consignment-value" />
            </Field>
            {/* Product Category */}
            <Field label="Product Category" required>
              <select value={productCategory} onChange={(e) => setProductCategory(e.target.value)} className={selectCls} data-testid="product-category">
                <option value="">— Select —</option>
                {productCategories.map(p => (
                  <option key={p.code} value={p.code || p.name}>{p.name}</option>
                ))}
              </select>
            </Field>
            {/* Service Type */}
            <Field label="Service Type">
              <select value={serviceType} onChange={(e) => setServiceType(e.target.value)} className={selectCls} data-testid="service-type">
                <option value="STANDARD">Standard</option>
                <option value="EXPRESS">Express</option>
                <option value="NDD">Next Day Delivery</option>
              </select>
            </Field>
            {/* Material Description */}
            <div className="sm:col-span-2">
              <Field label="Material Description">
                <input value={materialDesc} onChange={(e) => setMaterialDesc(e.target.value)}
                  placeholder="Contents of the package" className={inputCls} data-testid="material-desc" />
              </Field>
            </div>
            {/* Initial Weight */}
            <Field label="Initial Weight (kg)">
              <input type="number" step="0.01" value={initialWeight} onChange={(e) => setInitialWeight(e.target.value)}
                placeholder="0.00" className={inputCls} data-testid="initial-weight" />
            </Field>
            {/* COD Amount */}
            {paymentMode === "cod" && (
              <Field label="COD Amount (₹)" required>
                <input type="number" value={codAmount} onChange={(e) => setCodAmount(e.target.value)}
                  placeholder="0.00" className={inputCls} data-testid="cod-amount" />
              </Field>
            )}
          </div>

          {/* Insurance Toggle */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-semibold text-slate-700">Insurance</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${insuranceType === "CARRIER_RISK" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                  {insuranceType === "CARRIER_RISK" ? "Carrier Risk — Sendtoo Liable" : "Owner Risk — Customer Liable"}
                </span>
                {insuranceType === "CARRIER_RISK" && consignmentValue && (
                  <span className="text-xs text-blue-600 font-semibold">Premium: ₹{insurancePremium}</span>
                )}
              </div>
              <div className="flex rounded-sm border border-slate-200 overflow-hidden">
                <button onClick={() => setInsuranceType("CARRIER_RISK")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${insuranceType === "CARRIER_RISK" ? "bg-green-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  Opt In (Carrier Risk)
                </button>
                <button onClick={() => setInsuranceType("OWNER_RISK")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${insuranceType === "OWNER_RISK" ? "bg-amber-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  Opt Out (Owner Risk)
                </button>
              </div>
            </div>
          </div>

          {/* Eway Bill — conditional */}
          {requiresEwayBill && (
            <div className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
              <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Eway Bill required for shipments above ₹50,000
              </p>
              <Field label="Eway Bill Number" required>
               <input value={ewayBill} onChange={(e) => setEwayBill(e.target.value.replace(/\D/g, "").slice(0, 12))}
                  placeholder="Enter 12-digit Eway Bill Number"
                  maxLength={12}
                  className={ewayBill.length === 12 ? inputCls : inputErrorCls} data-testid="eway-bill-number" />
                {ewayBill && ewayBill.length !== 12 && (
                  <p className="text-xs text-red-600 mt-1">Eway Bill must be exactly 12 digits</p>
                )}
              </Field>
              <p className="text-xs text-amber-700 mt-2">
                💡 Select the appropriate Transporter ID (based on the carrier handling this shipment) and generate Part-A of the Eway Bill accordingly on the government portal.
              </p>
            </div>
          )}
        </div>

        {/* ── WEIGHT & DIMENSIONS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500">Weight & Dimensions</h2>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                <input type="checkbox" checked={addLater} onChange={(e) => setAddLater(e.target.checked)} className="rounded" data-testid="add-later-checkbox" />
                Add later / Post pickup
              </label>
              <div className="flex rounded-sm border border-slate-200 overflow-hidden">
                {[["single", "Single Box"], ["multi", "Multi Box"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => setBoxMode(val)} data-testid={`box-mode-${val}`}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${boxMode === val ? "bg-brand-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>

          {!addLater && (
            <>
              {/* Single Box */}
              {boxMode === "single" && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <Field label="Dead Weight (kg)">
                    <input type="number" step="0.01" value={deadWeight} onChange={(e) => setDeadWeight(e.target.value)} placeholder="0.00" className={inputCls} data-testid="dead-weight" />
                  </Field>
                  <Field label="Length (cm)">
                    <input type="number" step="0.1" value={length} onChange={(e) => setLength(e.target.value)} placeholder="0" className={inputCls} data-testid="box-length" />
                  </Field>
                  <Field label="Breadth (cm)">
                    <input type="number" step="0.1" value={breadth} onChange={(e) => setBreadth(e.target.value)} placeholder="0" className={inputCls} data-testid="box-breadth" />
                  </Field>
                  <Field label="Height (cm)">
                    <input type="number" step="0.1" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="0" className={inputCls} data-testid="box-height" />
                  </Field>
                  <Field label="Divisor">
                    <select value={divisor} onChange={(e) => setDivisor(Number(e.target.value))} className={selectCls} data-testid="divisor">
                      {DIVISORS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </Field>
                </div>
              )}

              {/* Multi Box */}
              {boxMode === "multi" && (
                <div>
                  {/* Same size toggle */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-semibold text-slate-600">Box entry mode:</span>
                    <div className="flex rounded-sm border border-slate-200 overflow-hidden">
                      {[["false", "Different Sizes"], ["true", "Same Size (All Boxes)"]].map(([val, lbl]) => (
                        <button key={val} onClick={() => setSameSize(val === "true")}
                          className={`px-3 py-1.5 text-xs font-semibold transition-colors ${String(sameSize) === val ? "bg-brand-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{lbl}</button>
                      ))}
                    </div>
                  </div>

                  {/* Same Size entry */}
                  {sameSize ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                        <Field label="Total Boxes">
                          <input type="number" min="1" value={totalBoxCount}
                            onChange={(e) => setTotalBoxCount(parseInt(e.target.value) || 1)} className={inputCls} />
                        </Field>
                        <Field label="L (cm)">
                          <input type="number" value={sameSizeData.l} onChange={(e) => setSameSizeData({ ...sameSizeData, l: e.target.value })} placeholder="0" className={inputCls} />
                        </Field>
                        <Field label="B (cm)">
                          <input type="number" value={sameSizeData.b} onChange={(e) => setSameSizeData({ ...sameSizeData, b: e.target.value })} placeholder="0" className={inputCls} />
                        </Field>
                        <Field label="H (cm)">
                          <input type="number" value={sameSizeData.h} onChange={(e) => setSameSizeData({ ...sameSizeData, h: e.target.value })} placeholder="0" className={inputCls} />
                        </Field>
                        <Field label="Dead Wt/box (kg)">
                          <input type="number" value={sameSizeData.deadWt} onChange={(e) => setSameSizeData({ ...sameSizeData, deadWt: e.target.value })} placeholder="0" className={inputCls} />
                        </Field>
                        <Field label="Divisor">
                          <select value={sameSizeData.divisor} onChange={(e) => setSameSizeData({ ...sameSizeData, divisor: Number(e.target.value) })} className={selectCls}>
                            {DIVISORS.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </Field>
                      </div>
                      <p className="text-xs text-blue-700 font-semibold mt-3">
                        {totalBoxCount} boxes × same dimensions = {totalBoxCount} AWBs will be generated
                      </p>
                    </div>
                  ) : (
                    /* Different sizes table */
                    <div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[600px] text-xs">
                          <thead className="bg-slate-50">
                            <tr>
                              {["#", "Boxes", "L (cm)", "B (cm)", "H (cm)", "Divisor", "Dead Wt", "Vol Wt", "Chargeable", ""].map(h => (
                                <th key={h} className="px-2 py-2 text-left font-bold text-slate-500 uppercase">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {boxes.map((box, idx) => {
                              const vw = calcVolWt(box.l, box.b, box.h, box.qty, box.divisor);
                              const dw = (parseFloat(box.deadWt) || 0) * (parseInt(box.qty) || 1);
                              const cw = Math.max(dw, vw);
                              return (
                                <tr key={box.id} className="border-t border-slate-100">
                                  <td className="px-2 py-1.5 font-semibold text-slate-500">{idx + 1}</td>
                                  <td className="px-2 py-1.5"><input type="number" min="1" value={box.qty} onChange={(e) => updateBox(box.id, "qty", e.target.value)} className="w-16 px-2 py-1 border border-slate-200 rounded text-xs" /></td>
                                  {["l", "b", "h"].map(f => (
                                    <td key={f} className="px-2 py-1.5"><input type="number" step="0.1" value={box[f]} onChange={(e) => updateBox(box.id, f, e.target.value)} placeholder="0" className="w-16 px-2 py-1 border border-slate-200 rounded text-xs" /></td>
                                  ))}
                                  <td className="px-2 py-1.5">
                                    <select value={box.divisor} onChange={(e) => updateBox(box.id, "divisor", Number(e.target.value))} className="w-16 px-1 py-1 border border-slate-200 rounded text-xs bg-white">
                                      {DIVISORS.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                  </td>
                                  <td className="px-2 py-1.5"><input type="number" step="0.01" value={box.deadWt} onChange={(e) => updateBox(box.id, "deadWt", e.target.value)} placeholder="0" className="w-16 px-2 py-1 border border-slate-200 rounded text-xs" /></td>
                                  <td className="px-2 py-1.5 font-semibold text-indigo-600">{vw.toFixed(2)}</td>
                                  <td className="px-2 py-1.5 font-bold text-brand-500">{cw.toFixed(2)}</td>
                                  <td className="px-2 py-1.5">
                                    <button onClick={() => removeBox(box.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                              <td colSpan={6} className="px-2 py-2 text-right text-slate-600">Totals:</td>
                              <td className="px-2 py-2 text-slate-700">{multiTotalDeadWt.toFixed(2)}</td>
                              <td className="px-2 py-2 text-indigo-600">{multiTotalVolWt.toFixed(2)}</td>
                              <td className="px-2 py-2 text-brand-500">{multiChargeableWt.toFixed(2)}</td>
                              <td />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <button onClick={addBox} className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-brand-500 hover:text-brand-600 px-2 py-1 rounded hover:bg-brand-50 transition-colors" data-testid="add-box-btn">
                        <Plus className="w-3.5 h-3.5" /> Add Box Row
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Weight summary */}
              {finalChargeableWt > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-3 bg-slate-50 rounded-lg p-3">
                  <div className="text-center">
                    <p className="text-xs text-slate-400 font-semibold uppercase">Volumetric Wt</p>
                    <p className="font-heading font-bold text-indigo-600">{(boxMode === "single" ? singleVolWt : sameSize ? sameSizeVolWt : multiTotalVolWt).toFixed(2)} kg</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 font-semibold uppercase">Dead Weight</p>
                    <p className="font-heading font-bold text-slate-700">{(boxMode === "single" ? singleDeadWt : sameSize ? sameSizeDeadWt : multiTotalDeadWt).toFixed(2)} kg</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 font-semibold uppercase">Chargeable Wt</p>
                    <p className="font-heading font-bold text-brand-500">{finalChargeableWt.toFixed(2)} kg</p>
                  </div>
                </div>
              )}
            </>
          )}

          {addLater && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
              Using initial weight: <strong>{initialWeight || "0"} kg</strong>. Exact dimensions will be updated after pickup.
            </div>
          )}

          {/* AWB count badge */}
          {boxMode === "multi" && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <span className="font-semibold text-amber-800">AWBs to be generated: {totalAwbCount}</span>
              <span className="text-amber-600 ml-2 text-xs">(one label per physical box)</span>
            </div>
          )}
        </div>

        {/* ── SHIPMENT PHOTOS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Shipment Photos</h2>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => photoRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm font-semibold text-slate-500 hover:border-brand-400 hover:text-brand-500 transition-colors">
              <Upload className="w-4 h-4" /> Upload Photos
            </button>
            <input ref={photoRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload" />
            {photos.map((p, i) => (
              <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                  className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="w-2.5 h-2.5 text-white" />
                </button>
              </div>
            ))}
          </div>
          {photos.length === 0 && <p className="text-xs text-slate-400 mt-2">Upload package photos (JPG, PNG, WEBP)</p>}
        </div>

        {/* ── BILLING DETAILS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Billing Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Final Chargeable Weight</label>
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-sm font-heading font-bold text-brand-500">
                {finalChargeableWt.toFixed(2)} kg
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Auto-Calculated Bill</label>
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm text-sm font-heading font-bold text-slate-700">
                {estimatedPrice ? `₹${estimatedPrice}` : "Calculating..."}
              </div>
            </div>
            <Field label="Bill Amount (Manual Override)">
              <input type="number" value={billOverride} onChange={(e) => setBillOverride(e.target.value)}
                placeholder="Override amount" className={`${inputCls} border-dashed`} data-testid="bill-override" />
            </Field>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Final Bill Amount</label>
              <div className={`px-3 py-2 border rounded-sm text-sm font-heading font-bold ${billOverride ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-green-50 border-green-300 text-green-700"}`}>
                ₹{finalBillAmount || 0}
                {billOverride && <span className="text-xs font-normal ml-1">(Override)</span>}
              </div>
            </div>
          </div>
        </div>

        {/* ── SPECIAL INSTRUCTIONS ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Special Instructions</h2>
          <textarea value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)}
            rows={3} placeholder="Any special handling instructions, fragile items, delivery notes..."
            className={`${inputCls} resize-none`} data-testid="special-instructions" />
        </div>

        {/* ── ACTION BUTTONS ── */}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={handleReset} data-testid="booking-reset-btn"
            className="flex items-center gap-2 px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm hover:bg-slate-50 uppercase">
            <RefreshCw className="w-4 h-4" /> Reset Form
          </button>
          <button onClick={handleSubmit} disabled={loading} data-testid="booking-console-submit"
            className="flex items-center gap-2 px-8 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70 uppercase shadow-sm">
            <Send className="w-4 h-4" /> {loading ? "Submitting..." : "Submit Order"}
          </button>
        </div>
      </div>

      {/* ── LOCATION MISMATCH MODAL ── */}
      {showLocationWarning && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Location Mismatch</h3>
                <p className="text-sm text-slate-500 mt-1">
                  This pickup location is not registered under <strong>{selectedCustomer?.name}</strong>. Proceed anyway?
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowLocationWarning(false); setPendingWarehouse(""); }}
                className="px-4 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={() => { setWarehouse(pendingWarehouse); setShowLocationWarning(false); setPendingWarehouse(""); }}
                className="px-4 py-2 text-sm font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-600">
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
